import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { SelectGroupMembersDialogComponent } from 'app/library/group/components/dialogs/select-group-members-dialog/select-group-members-dialog.component';
import { IAddTeamWatchersActionPayload } from 'app/library/team/models/add-team-watchers-action.action.payload';
import { IRemoveTeamWatchersActionPayload } from 'app/library/team/models/remove-team-watchers-action.action.payload';
import { Team } from 'app/library/team/models/team.model';
import { TeamService } from 'app/library/team/services/team.service';
import { TrackTeamSubTeamProgressService } from 'app/library/team/services/track-team-subteam-progress.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { MoveToDialogComponent, MOVE_TO_ROOT_DESTINATION_ID } from 'app/projects/shared/src/lib/components/dialogs/move-to-dialog/move-to-dialog.component';
import { NameDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/name/name.component';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-team-page',
    templateUrl: './team-page.component.html',
    styleUrls: ['./team-page.component.scss'],
    animations: fuseAnimations,
})
export class TeamPageComponent extends UnsubscribeOnDestroy implements OnInit {
    @Select(AllUsersState.getEntities)
    allUser$: Observable<User[]>;

    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    moveToDialogRef: MatDialogRef<MoveToDialogComponent>;

    teamId: string;
    isTrackSubTeamProgressActive: boolean;
    isCurrentUserTenantAdmin: boolean;
    profile = this._store.selectSnapshot(UserState.getMyInfo);

    constructor(
        public teamService: TeamService,
        private _router: Router,
        private _matDialog: MatDialog,
        private _translateService: TranslateService,
        private _activatedRoute: ActivatedRoute,
        private _trackTeamSubTeamProgressService: TrackTeamSubTeamProgressService,
        private _store: Store
    ) {
        super();
    }

    ngOnInit(): void {
        this.isCurrentUserTenantAdmin = this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT);

        this._activatedRoute.params.pipe(takeUntil(this._unsubscribeAll)).subscribe((params) => {
            this.teamId = params['teamId'];
            const includeSubTeamsProgress = !this._trackTeamSubTeamProgressService.isTrackTeamSubTeamProgressExcluded(this.teamId);
            this.isTrackSubTeamProgressActive = includeSubTeamsProgress;
        });
    }

    teamProgressChanged(includeSubTeamsProgress: boolean): void {
        this.isTrackSubTeamProgressActive = includeSubTeamsProgress;
    }

    async renameTeam(team: Team, teamName: string): Promise<void> {
        team.name = teamName;
        await this.teamService.updateTeam(team);
        // Refetch team progress
        await this.teamService.fetchTeamWithSubTeamsProgress(this.teamId, this.isTrackSubTeamProgressActive);
    }

    openRenameTeamDialog(team: Team): void {
        const dialogRef = this._matDialog.open(NameDialogComponent, {
            disableClose: false,
            width: '482px',
        });

        dialogRef.componentInstance.name = team.name;
        dialogRef.componentInstance.dialogTitle = 'GENERAL.BUTTON.RENAME';
        dialogRef.componentInstance.placeholder = 'GENERAL.PROPERTY.NAME';

        dialogRef.componentInstance.save.subscribe(async (name: string) => {
            team.name = name;
            await this.teamService.updateTeam(team);
            // Refetch team progress
            await this.teamService.fetchTeamWithSubTeamsProgress(this.teamId, this.isTrackSubTeamProgressActive);
            dialogRef.close();
        });
    }

    async getTeamWatchers(teamId: string, menuTrigger: MatMenuTrigger): Promise<void> {
        await this.teamService.fetchTeamWatchers(teamId);
        menuTrigger.openMenu();
    }

    openRemoveTeamWatcherDialog(teamWatcher: User, team: Team): void {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('TEAMS.DIALOG.REMOVE_TEAM_WATCHER.MESSAGE', {
            fullName: teamWatcher.fullName,
        });
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        this.confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                const payload: IRemoveTeamWatchersActionPayload = {
                    userIds: [teamWatcher.id],
                    teamId: team.id,
                };

                const isRemoved = await this.teamService.removeTeamWatchers(payload);
                if (isRemoved) {
                    team.watcherCount--;
                    const profile = this._store.selectSnapshot(UserState.getMyInfo);
                    const isCurrentUserTeamWatcher = payload.userIds.includes(profile.id);
                    if (isCurrentUserTeamWatcher) {
                        team.isCurrentUserWatcher = false;
                    }
                }
            }

            this.confirmDialogRef = null;
        });
    }

    openAddWatcherDialog(team: Team): void {
        const dialogRef = this._matDialog.open(SelectGroupMembersDialogComponent, {
            width: '680px',
            id: 'select-users-dialog',
        });

        dialogRef.componentInstance.entitie$ = this.allUser$;
        dialogRef.componentInstance.entityId = this.teamId;
        dialogRef.componentInstance.title = 'TEAMS.WATCHER.BUTTON.ADD_WATCHER';
        dialogRef.componentInstance.dataType = 'users';
        dialogRef.componentInstance.role = 'teamWatcher';

        dialogRef.afterClosed().subscribe(async (result: { list: User[] }) => {
            if (result && result.list) {
                const payload: IAddTeamWatchersActionPayload = {
                    userIds: result.list.map((user) => user.id),
                    teamIds: [team.id],
                };

                const isSuccess = await this.teamService.addUsersToTeamAsWatchers(payload);
                if (isSuccess) {
                    team.watcherCount += result.list.length;
                    const profile = this._store.selectSnapshot(UserState.getMyInfo);
                    const isCurrentUserTeamWatcher = payload.userIds.includes(profile.id);
                    if (isCurrentUserTeamWatcher) {
                        team.isCurrentUserWatcher = true;
                    }
                }
            }
        });
    }

    async stopWatchingTeam(team: Team): Promise<void> {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
            width: '600px',
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'TEAMS.WATCHER.DIALOG.STOP_WATCHING.TITLE';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = 'TEAMS.WATCHER.DIALOG.STOP_WATCHING.MESSAGE';
        this.confirmDialogRef.componentInstance.actionButtonText = 'TEAMS.WATCHER.BUTTON.STOP_WATCHING';

        this.confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                const isRemoved = await this.teamService.stopWatchingTeam(team.id);
                if (isRemoved) {
                    team.watcherCount--;
                    team.isCurrentUserWatcher = false;
                }

                this.confirmDialogRef = null;
            }
        });
    }

    async openMoveToDialog(team: Team): Promise<void> {
        this.moveToDialogRef = this._matDialog.open(MoveToDialogComponent, {
            disableClose: false,
            width: '760px',
            autoFocus: false,
        });

        this.moveToDialogRef.componentInstance.title = this._translateService.instant('GENERAL.LABEL.MOVE_TO', {
            entity: team.name,
        });
        this.moveToDialogRef.componentInstance.entity = team;
        this.moveToDialogRef.componentInstance.allowMoveToRoot = !!team.parent;

        this.moveToDialogRef.afterClosed().subscribe(async (selectedTeam: Team) => {
            if (selectedTeam) {
                if (selectedTeam.id === MOVE_TO_ROOT_DESTINATION_ID) {
                    team.parent = null;
                } else {
                    team.parent = team.parent ? { ...team.parent, id: selectedTeam.id } : { id: selectedTeam.id };
                }
                await this.teamService.updateTeam(team);
                await this.teamService.fetchRootTeams();
            }

            this.moveToDialogRef = null;
        });

        let rootTeams = this.teamService.getRootTeams();
        const needsFetch = !rootTeams?.length;
        this.moveToDialogRef.componentInstance.isLoading = needsFetch;

        if (needsFetch) {
            await this.teamService.fetchRootTeams();
            if (!this.moveToDialogRef) {
                return;
            }
            rootTeams = this.teamService.getRootTeams() || [];
        }

        const teamListTree = this.teamService.convertListToTree(rootTeams || []).map((rootTeam) => this.teamService.mapTeamToEntityNode(rootTeam));

        this.moveToDialogRef.componentInstance.lazyLoadChildren = async (node) => {
            const children = await this.teamService.fetchParentChildren(node.id);
            if (!children) {
                return [];
            }

            return this.teamService.convertListToTree(children).map((child) => this.teamService.mapTeamToEntityNode(child));
        };
        this.moveToDialogRef.componentInstance.listTree = teamListTree;
        this.moveToDialogRef.componentInstance.isLoading = false;
    }

    openDeleteTeamDialog(team: Team): void {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
            width: '600px',
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'TEAMS.DIALOG.DELETE_TEAM.TITLE';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('TEAMS.DIALOG.DELETE_TEAM.MESSAGE', {
            teamName: team.name,
        });
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        this.confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                const isRemoved = await this.teamService.deleteTeam(this.teamId);
                if (isRemoved) {
                    // Collapse the sidebar tree (and clear the sidebar's selectedTeam) BEFORE refetching
                    // root teams, so the sidebar's rootTeams$ subscription does not re-open the deleted
                    // team's parent path via reconstructTree.
                    this.teamService.collapseAllTeams();
                    await this.teamService.fetchRootTeams();
                    this._router.navigate(['admin/people-development/teams/edit']);
                }
                this.confirmDialogRef = null;
            }
        });
    }

    openCreateNewTeamDialog(team: Team): void {
        const dialogRef = this._matDialog.open(NameDialogComponent, {
            disableClose: false,
            width: '482px',
        });

        dialogRef.componentInstance.dialogTitle = 'TEAMS.DIALOG.CREATE_NEW_SUBTEAM.TITLE';
        dialogRef.componentInstance.placeholder = 'GENERAL.PROPERTY.NAME';

        dialogRef.componentInstance.save.subscribe(async (teamName: string) => {
            if (teamName) {
                const newTeam = {
                    name: teamName,
                    parent: team ? { id: team.id } : null,
                } as Team;

                await this.teamService.createTeam(newTeam);

                dialogRef.close();
            }
        });
    }
}
